import { type QueryClient, type QueryKey, useQuery, useQueryClient } from "@tanstack/react-query";

import type { Artifact, ArtifactOperation, ArtifactOperationType } from "@virtual-brain-lib/types";
import {
	hasArtifactProcessingOperation,
	hasArtifactProcessingOperationOfType,
} from "@virtual-brain-lib/utils-common";

import { getArtifact } from "../api/artifact";

const OPTIMISTIC_OPERATION_PREFIX = "optimistic-";

function artifactQueryKey(artifactId: Artifact["id"]): QueryKey {
	return getArtifact({ params: { artifactId } }).queryKey;
}

export function useArtifactQuery(artifactId: Artifact["id"]) {
	const { data: artifact } = useQuery({
		...getArtifact({ params: { artifactId } }),
		enabled: Boolean(artifactId),
	});

	const { operations } = artifact ?? {};

	return {
		artifact,
		hasArtifactProcessingOperation: hasArtifactProcessingOperation(operations),
		hasArtifactProcessingOperationOfType: (type: ArtifactOperationType) =>
			hasArtifactProcessingOperationOfType({ operations, type }),
	};
}

export function useArtifactMutations() {
	const queryClient = useQueryClient();

	const updateArtifact = ({
		artifactId,
		updates,
	}: {
		artifactId: Artifact["id"];
		updates: Partial<Artifact>;
	}) => {
		queryClient.setQueryData<Artifact>(artifactQueryKey(artifactId), (prev) =>
			prev ? { ...prev, ...updates } : undefined,
		);
	};

	const addOptimisticProcessingOperationToCache = ({
		artifactId,
		type,
	}: {
		artifactId: Artifact["id"];
		type: ArtifactOperationType;
	}) => {
		queryClient.setQueryData<Artifact>(artifactQueryKey(artifactId), (prev) => {
			if (!prev) {
				return prev;
			}

			const operations = prev.operations ?? [];
			const alreadyProcessing = operations.some(
				(operation) => operation.type === type && operation.status === "processing",
			);

			if (alreadyProcessing) {
				return prev;
			}

			const now = new Date();
			const optimisticOperation: ArtifactOperation = {
				id: `${OPTIMISTIC_OPERATION_PREFIX}${type}-${now.getTime()}`,
				artifactId,
				type,
				status: "processing",
				startedAt: now,
				updatedAt: now,
			};

			return { ...prev, operations: [...operations, optimisticOperation] };
		});
	};

	/** Upserts by id, replacing a same-type optimistic placeholder if one exists. */
	const upsertArtifactOperationInCache = ({
		artifactId,
		operationId,
		type,
		status,
	}: {
		artifactId: Artifact["id"];
		operationId: string;
		type: ArtifactOperationType;
		status: ArtifactOperation["status"];
	}) => {
		queryClient.setQueryData<Artifact>(artifactQueryKey(artifactId), (prev) => {
			if (!prev) {
				return prev;
			}

			const operations = prev.operations ?? [];
			const now = new Date();

			if (operations.some((operation) => operation.id === operationId)) {
				return {
					...prev,
					operations: operations.map((operation) =>
						operation.id === operationId
							? { ...operation, status, updatedAt: now }
							: operation,
					),
				};
			}

			const realOperation: ArtifactOperation = {
				id: operationId,
				artifactId,
				type,
				status,
				startedAt: now,
				updatedAt: now,
			};

			const optimisticIndex = operations.findIndex(
				(operation) =>
					operation.id.startsWith(OPTIMISTIC_OPERATION_PREFIX) &&
					operation.type === type &&
					operation.status === "processing",
			);

			if (optimisticIndex !== -1) {
				const next = [...operations];

				next[optimisticIndex] = {
					...realOperation,
					startedAt: operations[optimisticIndex].startedAt,
				};

				return { ...prev, operations: next };
			}

			return { ...prev, operations: [...operations, realOperation] };
		});
	};

	/** Sets a row's status by id; falls back to processing rows of `type` when no id is given. */
	const setArtifactOperationStatusInCache = ({
		artifactId,
		operationId,
		type,
		status,
	}: {
		artifactId: Artifact["id"];
		operationId: string;
		type: ArtifactOperationType | undefined;
		status: Extract<ArtifactOperation["status"], "processed" | "failed">;
	}) => {
		queryClient.setQueryData<Artifact>(artifactQueryKey(artifactId), (prev) => {
			if (!prev?.operations) {
				return prev;
			}

			const matches = (operation: ArtifactOperation) =>
				operationId
					? operation.id === operationId
					: operation.status === "processing" && operation.type === type;

			return {
				...prev,
				operations: prev.operations.map((operation) =>
					matches(operation)
						? { ...operation, status, updatedAt: new Date() }
						: operation,
				),
			};
		});
	};

	return {
		updateArtifact,
		addOptimisticProcessingOperationToCache,
		upsertArtifactOperationInCache,
		setArtifactOperationStatusInCache,
	};
}

export function getHasCachedArtifactProcessingOperation({
	queryClient,
	artifactId,
}: {
	queryClient: QueryClient;
	artifactId: Artifact["id"];
}): boolean {
	if (!artifactId) {
		return false;
	}

	const artifact = queryClient.getQueryData<Artifact>(artifactQueryKey(artifactId));

	return hasArtifactProcessingOperation(artifact?.operations);
}
